import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

/**
 * Hook personalizado para manejar la autenticación
 * El login es manejado externamente por el servicio functions.js
 */
export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  // Ref para evitar múltiples ejecuciones simultáneas
  const isCheckingAuth = useRef(false);
  const hasCheckedAuth = useRef(false);

  /**
   * Verifica la autenticación del usuario
   */
  const checkAuth = async () => {
    
    // Evitar múltiples ejecuciones simultáneas
    if (isCheckingAuth.current) {
      console.log('🔍 useAuth.checkAuth - Ya en proceso, saltando...');
      return;
    }
    
    isCheckingAuth.current = true;
    console.group('🔍 useAuth.checkAuth - Iniciando verificación');
    
    try {
      setLoading(true);
      setError(null);
      
      // Verificar parámetros de URL primero
      const urlParams = new URLSearchParams(window.location.search);
      const uid = urlParams.get('uid');
      
      console.log('Parámetros de URL:', {
        uid,
        redirect: urlParams.get('redirect'),
        hasOtherParams: urlParams.toString()
      });

      // Si hay UID en la URL, verificar token por UID
      if (uid) {
        console.log('🎯 UID encontrado en URL:', uid);
        
        // Verificar primero si ya hay un token válido en localStorage
        const existingToken = localStorage.getItem('sanctum_token');
        const existingUserData = localStorage.getItem('user_data');
        
        if (existingToken && existingUserData) {
          console.log('✅ Token ya existe en localStorage, verificando validez...');
          
          try {
            // Verificar que el token existente sea válido
            const authResult = await authService.checkAuthStatus();
            
            if (authResult && authResult.user) {
              console.log('✅ Token existente es válido, usando datos locales');
              setIsAuthenticated(true);
              setUser(authResult.user);
              setLoading(false);
              hasCheckedAuth.current = true;
              
              // Limpiar UID de la URL ya que no es necesario
              urlParams.delete('uid');
              const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
              window.history.replaceState({}, document.title, newUrl);
              
              console.groupEnd();
              return true;
            } else {
              console.warn('⚠️ Token existente no es válido, obteniendo nuevo token...');
            }
          } catch (error) {
            console.warn('⚠️ Error verificando token existente, obteniendo nuevo token:', error.message);
          }
        } else {
          console.log('🔍 No hay token en localStorage, obteniendo token por UID');
        }
        
        // Solo llamar al backend si no hay token válido en localStorage
        console.log('🚀 Verificando token por UID en backend:', uid);
        const tokenResult = await authService.checkUserTokenByUID(parseInt(uid));
        
        if (tokenResult && tokenResult.token) {
          console.log('✅ Token obtenido por UID exitosamente');
          setIsAuthenticated(true);
          setUser(tokenResult.user);
          setLoading(false);
          hasCheckedAuth.current = true;
          
          // Limpiar UID de la URL
          urlParams.delete('uid');
          const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
          window.history.replaceState({}, document.title, newUrl);
          
          console.groupEnd();
          return true;
        } else {
          console.warn('❌ Usuario no tiene token válido disponible');
          // Redirigir porque no hay token válido
          const redirectUrl = urlParams.get("redirect");
          redirect(redirectUrl);
          console.groupEnd();
          return false;
        }
      }
      
      console.log('Estado inicial:', {
        sanctumToken: localStorage.getItem('sanctum_token'),
        userData: localStorage.getItem('user_data'),
        hasToken: !!localStorage.getItem('sanctum_token')
      });

      // Verificar si hay un token válido en el backend
      console.log('🚀 Verificando token con el backend...');
      const authResult = await authService.checkAuthStatus();
      
      console.log('Resultado de checkAuthStatus:', authResult);
      
      if (authResult && authResult.user) {
        console.log('✅ Autenticación exitosa desde backend');
        setIsAuthenticated(true);
        setUser(authResult.user);
        setLoading(false);
        hasCheckedAuth.current = true;
        console.groupEnd();
        return true;
      }

      // Obtener datos de usuario desde localStorage si no se pudieron obtener del backend
      console.log('💾 Verificando datos locales...');
      const localUserData = authService.getUserData();
      const hasToken = authService.hasToken();
      
      console.log('Datos locales:', {
        localUserData,
        hasToken
      });
      
      if (localUserData && hasToken) {
        console.log('✅ Usando datos locales para autenticación');
        setIsAuthenticated(true);
        setUser(localUserData);
        setLoading(false);
        hasCheckedAuth.current = true;
        console.groupEnd();
        return true;
      }

      // Si llegamos aquí, no hay autenticación válida
      console.warn('❌ No hay autenticación válida');
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
      hasCheckedAuth.current = true;
      
      // Redirigir al dashboard principal si no está autenticado
      const redirectUrl = urlParams.get("redirect");
      console.log('🔄 Redirigiendo...', { redirectUrl });
      redirect(redirectUrl);
      
      console.groupEnd();
      return false;

    } catch (err) {
      console.error('❌ Error verificando autenticación:', err);
      setError('Error verificando autenticación: ' + err.message);
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
      hasCheckedAuth.current = true;
      console.groupEnd();
      return false;
    } finally {
      isCheckingAuth.current = false;
    }
  };

  /**
   * Logout del usuario
   */
  const logout = async () => {
    try {
      await authService.logout();
      setIsAuthenticated(false);
      setUser(null);
      setError(null);
      hasCheckedAuth.current = false;
      
      // Redirigir al login o página principal
      const urlParams = new URLSearchParams(window.location.search);
      const redirectUrl = urlParams.get("redirect");
      redirect(redirectUrl);
    } catch (err) {
      console.error('Error en logout:', err);
      // Aún así limpiar el estado local
      setIsAuthenticated(false);
      setUser(null);
      hasCheckedAuth.current = false;
      authService.removeToken();
    }
  };

  /**
   * Redirigir a la página de login/dashboard
   */
  const redirect = (redirectUrl) => {
    if (redirectUrl) {
      window.location.href = redirectUrl + 'dashboard/';
    } else {
      navigate("/");
    }
  };

  /**
   * Verificar autenticación al montar el componente
   * Solo se ejecuta una vez y evita re-ejecuciones innecesarias
   */
  useEffect(() => {
    // Solo ejecutar si no se ha verificado antes
    if (!hasCheckedAuth.current && !isCheckingAuth.current) {
      checkAuth();
    }
  }, []); // Array de dependencias vacío para ejecutar solo una vez

  return {
    isAuthenticated,
    user,
    loading,
    error,
    logout,
    checkAuth
  };
};

export default useAuth;